/**
 * DataManagementPanel
 * Allows admin users to view and edit the facman2 reference data that drives
 * the scheduler: instructors, credentials (who can teach what), and slot
 * time preferences.
 *
 * Three sub-tabs:
 *   Instructors   — toggle DoNotSchedule, edit SchedulingNotes, FacultyType
 *   Credentials   — view instructor→course mappings; add / remove
 *   Slot Prefs    — instructor × time-slot preference matrix (1/2/x)
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  schedulerApi,
  InstructorData,
  CredentialData,
  SlotPreferenceData,
  SlotInfo,
  InstructorLookup,
  CourseLookup,
  getSlotLabel,
  PrerequisiteData,
  EquivalencyData,
  SequenceData,
} from '../../services/scheduler-api';

// ─── Shared style tokens ────────────────────────────────────────────────────

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

const BTN = (variant: 'primary' | 'secondary' | 'danger') => ({
  padding: '6px 14px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600' as const,
  cursor: 'pointer',
  background:
    variant === 'primary'
      ? 'var(--accent)'
      : variant === 'danger'
      ? 'rgba(248,113,113,0.15)'
      : 'var(--bg-secondary)',
  color:
    variant === 'primary'
      ? 'var(--button-text)'
      : variant === 'danger'
      ? '#f87171'
      : 'var(--text-primary)',
  border: variant === 'secondary' ? '1px solid var(--card-border)' : 'none',
});

const INPUT = {
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid var(--card-border)',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: '13px',
} as const;

const TH = {
  padding: '10px 12px',
  textAlign: 'left' as const,
  color: 'var(--text-secondary)',
  fontWeight: '600' as const,
  fontSize: '12px',
  whiteSpace: 'nowrap' as const,
  borderBottom: '2px solid var(--card-border)',
};

const TD = {
  padding: '10px 12px',
  fontSize: '13px',
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--card-border)',
};

// ─── Helper ─────────────────────────────────────────────────────────────────

const PREF_LABELS: Record<string, string> = {
  '1': '⭐ Preferred',
  '2': '✅ Available',
  x: '🚫 Unavailable',
};

function fmtTime(t: string): string {
  if (!t) return '';
  const s = t.toString().padStart(4, '0');
  const h = parseInt(s.slice(0, 2), 10);
  const m = s.slice(2);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${period}`;
}

// ─── Instructors sub-tab ────────────────────────────────────────────────────

function InstructorsTab() {
  const [instructors, setInstructors] = useState<InstructorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);  // row_key being saved
  const [editing, setEditing] = useState<Record<string, Partial<InstructorData>>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schedulerApi.getInstructors();
      setInstructors(data.instructors);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load instructors');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (instr: InstructorData) => {
    setEditing(prev => ({
      ...prev,
      [instr.row_key]: {
        do_not_schedule: instr.do_not_schedule,
        scheduling_notes: instr.scheduling_notes ?? '',
        faculty_type: instr.faculty_type,
        primary_area: instr.primary_area ?? '',
      },
    }));
  };

  const cancelEdit = (rowKey: string) => {
    setEditing(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const saveEdit = async (rowKey: string) => {
    const fields = editing[rowKey];
    if (!fields) return;
    setSaving(rowKey);
    try {
      await schedulerApi.updateInstructor(rowKey, fields);
      setInstructors(prev =>
        prev.map(i => i.row_key === rowKey ? { ...i, ...fields } as InstructorData : i)
      );
      cancelEdit(rowKey);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const filtered = instructors.filter(i =>
    !searchTerm || i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading instructors…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          placeholder="Search by name or email…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...INPUT, width: '260px' }}
        />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {filtered.length} instructor{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Name</th>
              <th style={TH}>Email</th>
              <th style={TH}>Type</th>
              <th style={TH}>Primary Area</th>
              <th style={TH}>Do Not Schedule</th>
              <th style={TH}>Notes</th>
              <th style={TH}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(instr => {
              const isEditing = !!editing[instr.row_key];
              const draft = editing[instr.row_key] ?? {};

              return (
                <tr key={instr.row_key}>
                  <td style={TD}>{instr.name}</td>
                  <td style={{ ...TD, color: 'var(--text-secondary)', fontSize: '12px' }}>{instr.email}</td>
                  <td style={TD}>
                    {isEditing ? (
                      <select
                        value={draft.faculty_type ?? instr.faculty_type}
                        onChange={e => setEditing(prev => ({ ...prev, [instr.row_key]: { ...prev[instr.row_key], faculty_type: e.target.value as 'FT' | 'Adjunct' } }))}
                        style={INPUT}
                      >
                        <option value="FT">FT</option>
                        <option value="Adjunct">Adjunct</option>
                      </select>
                    ) : (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: instr.faculty_type === 'FT' ? 'rgba(74,222,128,0.15)' : 'rgba(148,163,184,0.15)',
                        color: instr.faculty_type === 'FT' ? '#4ade80' : 'var(--text-secondary)',
                      }}>
                        {instr.faculty_type}
                      </span>
                    )}
                  </td>
                  <td style={TD}>
                    {isEditing ? (
                      <input
                        value={(draft.primary_area as string) ?? (instr.primary_area ?? '')}
                        onChange={e => setEditing(prev => ({ ...prev, [instr.row_key]: { ...prev[instr.row_key], primary_area: e.target.value } }))}
                        style={{ ...INPUT, width: '120px' }}
                        placeholder="e.g. IT, Business…"
                      />
                    ) : (
                      instr.primary_area ? (
                        <span style={{ padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: 'rgba(var(--accent-rgb,255,215,0),0.12)', border: '1px solid rgba(var(--accent-rgb,255,215,0),0.2)', color: 'var(--text-secondary)' }}>
                          {instr.primary_area}
                        </span>
                      ) : <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={TD}>
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={draft.do_not_schedule ?? instr.do_not_schedule}
                        onChange={e => setEditing(prev => ({ ...prev, [instr.row_key]: { ...prev[instr.row_key], do_not_schedule: e.target.checked } }))}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                    ) : (
                      instr.do_not_schedule
                        ? <span style={{ color: '#f87171', fontSize: '12px' }}>🚫 Yes</span>
                        : <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={{ ...TD, maxWidth: '260px' }}>
                    {isEditing ? (
                      <input
                        value={(draft.scheduling_notes as string) ?? (instr.scheduling_notes ?? '')}
                        onChange={e => setEditing(prev => ({ ...prev, [instr.row_key]: { ...prev[instr.row_key], scheduling_notes: e.target.value } }))}
                        style={{ ...INPUT, width: '100%' }}
                        placeholder="Optional notes…"
                      />
                    ) : (
                      <span style={{ color: instr.scheduling_notes ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: '12px' }}>
                        {instr.scheduling_notes || '—'}
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          style={BTN('primary')}
                          onClick={() => saveEdit(instr.row_key)}
                          disabled={saving === instr.row_key}
                        >
                          {saving === instr.row_key ? '…' : '💾 Save'}
                        </button>
                        <button style={BTN('secondary')} onClick={() => cancelEdit(instr.row_key)}>Cancel</button>
                      </div>
                    ) : (
                      <button style={BTN('secondary')} onClick={() => startEdit(instr)}>✏️ Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Credentials sub-tab ────────────────────────────────────────────────────

function CredentialsTab() {
  const [credentials, setCredentials] = useState<CredentialData[]>([]);
  const [instructors, setInstructors] = useState<InstructorLookup[]>([]);
  const [courses, setCourses] = useState<CourseLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [addInstr, setAddInstr] = useState('');
  const [addCourse, setAddCourse] = useState('');
  const [adding, setAdding] = useState(false);
  const [filterInstr, setFilterInstr] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schedulerApi.getCredentials();
      setCredentials(data.credentials);
      setInstructors(data.instructors);
      setCourses(data.courses);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rowKey: string) => {
    if (!window.confirm('Remove this credential?')) return;
    setDeleting(rowKey);
    try {
      await schedulerApi.deleteCredential(rowKey);
      setCredentials(prev => prev.filter(c => c.row_key !== rowKey));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleAdd = async () => {
    if (!addInstr || !addCourse) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await schedulerApi.addCredential(addInstr, addCourse);
      // Reload to get enriched data
      const instr = instructors.find(i => i.row_key === addInstr);
      const course = courses.find(c => c.row_key === addCourse);
      setCredentials(prev => [...prev, {
        row_key: res.row_key,
        instructor_row_key: addInstr,
        course_row_key: addCourse,
        instructor_name: instr?.name ?? addInstr,
        instructor_email: instr?.email ?? '',
        course_code: course?.code ?? addCourse,
      }].sort((a, b) => a.instructor_name.localeCompare(b.instructor_name) || a.course_code.localeCompare(b.course_code)));
      setAddInstr('');
      setAddCourse('');
    } catch (e: any) {
      setAddError(e?.response?.data?.error || e?.message || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const filtered = credentials.filter(c => {
    const mi = !filterInstr || c.instructor_name.toLowerCase().includes(filterInstr.toLowerCase()) || c.instructor_email.toLowerCase().includes(filterInstr.toLowerCase());
    const mc = !filterCourse || c.course_code.toLowerCase().includes(filterCourse.toLowerCase());
    return mi && mc;
  });

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading credentials…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      {/* Add new credential */}
      <div style={{ ...CARD, marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px', color: 'var(--text-primary)' }}>Add Credential</h4>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Instructor</label>
            <select value={addInstr} onChange={e => setAddInstr(e.target.value)} style={{ ...INPUT, minWidth: '200px' }}>
              <option value="">— Select instructor —</option>
              {instructors.map(i => (
                <option key={i.row_key} value={i.row_key}>{i.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Course</label>
            <select value={addCourse} onChange={e => setAddCourse(e.target.value)} style={{ ...INPUT, minWidth: '150px' }}>
              <option value="">— Select course —</option>
              {courses.map(c => (
                <option key={c.row_key} value={c.row_key}>{c.code}</option>
              ))}
            </select>
          </div>
          <button
            style={BTN('primary')}
            onClick={handleAdd}
            disabled={!addInstr || !addCourse || adding}
          >
            {adding ? '…' : '+ Add'}
          </button>
        </div>
        {addError && <p style={{ margin: '0.5rem 0 0 0', color: '#f87171', fontSize: '12px' }}>❌ {addError}</p>}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Filter instructor…" value={filterInstr} onChange={e => setFilterInstr(e.target.value)} style={{ ...INPUT, width: '200px' }} />
        <input placeholder="Filter course…" value={filterCourse} onChange={e => setFilterCourse(e.target.value)} style={{ ...INPUT, width: '140px' }} />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{filtered.length} credential{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Instructor</th>
              <th style={TH}>Email</th>
              <th style={TH}>Course</th>
              <th style={TH}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.row_key}>
                <td style={TD}>{c.instructor_name}</td>
                <td style={{ ...TD, fontSize: '12px', color: 'var(--text-secondary)' }}>{c.instructor_email}</td>
                <td style={TD}>
                  <span style={{ fontWeight: '600', color: 'var(--accent)' }}>{c.course_code.toUpperCase()}</span>
                </td>
                <td style={TD}>
                  <button
                    style={BTN('danger')}
                    onClick={() => handleDelete(c.row_key)}
                    disabled={deleting === c.row_key}
                  >
                    {deleting === c.row_key ? '…' : '🗑 Remove'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...TD, textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                  No credentials found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Slot Preferences sub-tab ───────────────────────────────────────────────

function SlotPrefsTab() {
  const [preferences, setPreferences] = useState<SlotPreferenceData[]>([]);
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [instructors, setInstructors] = useState<InstructorLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pending changes: key = `${instrRk}|${slotRk}`, value = preference ('1'|'2'|'x'|'')
  const [pending, setPending] = useState<Record<string, '1' | '2' | 'x' | ''>>({});

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schedulerApi.getSlotPreferences();
      setPreferences(data.preferences);
      setSlots(data.slots);
      setInstructors(data.instructors);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  // Build a lookup: instrRk + slotRk → current preference
  const prefMap: Record<string, '1' | '2' | 'x' | ''> = {};
  preferences.forEach(p => {
    prefMap[`${p.instructor_row_key}|${p.slot_row_key}`] = p.preference as '1' | '2' | 'x' | '';
  });

  const getPref = (instrRk: string, slotRk: string): '1' | '2' | 'x' | '' => {
    const key = `${instrRk}|${slotRk}`;
    return pending[key] !== undefined ? pending[key] : (prefMap[key] ?? '');
  };

  const setPref = (instrRk: string, slotRk: string, val: '1' | '2' | 'x' | '') => {
    const key = `${instrRk}|${slotRk}`;
    const original = prefMap[key] ?? '';
    if (val === original) {
      // Revert to saved — remove from pending
      setPending(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setPending(prev => ({ ...prev, [key]: val }));
    }
  };

  const hasPendingChanges = Object.keys(pending).length > 0;

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const requests = Object.entries(pending).map(([key, pref]) => {
        const [instrRk, slotRk] = key.split('|');
        return schedulerApi.upsertSlotPreference(instrRk, slotRk, pref);
      });
      await Promise.all(requests);
      // Reload to get fresh data
      await load();
      setPending({});
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filteredInstrs = instructors.filter(i =>
    !searchTerm || i.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading slot preferences…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  const PREF_COLORS: Record<string, string> = {
    '1': 'rgba(74,222,128,0.15)',
    '2': 'rgba(250,204,21,0.15)',
    x: 'rgba(248,113,113,0.15)',
    '': 'transparent',
  };
  const PREF_TEXT_COLORS: Record<string, string> = {
    '1': '#4ade80',
    '2': '#facc15',
    x: '#f87171',
    '': 'var(--text-secondary)',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Search instructor…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ ...INPUT, width: '220px' }}
          />
          <div style={{ display: 'flex', gap: '1rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span style={{ color: '#4ade80' }}>⭐ Preferred</span>
            <span style={{ color: '#facc15' }}>✅ Available</span>
            <span style={{ color: '#f87171' }}>🚫 Unavailable</span>
            <span>— Not set</span>
          </div>
        </div>
        {hasPendingChanges && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#facc15' }}>
              {Object.keys(pending).length} unsaved change{Object.keys(pending).length !== 1 ? 's' : ''}
            </span>
            <button style={BTN('primary')} onClick={saveAll} disabled={saving}>
              {saving ? '⏳ Saving…' : '💾 Save All'}
            </button>
            <button style={BTN('secondary')} onClick={() => setPending({})} disabled={saving}>
              Discard
            </button>
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ ...TH, minWidth: '160px' }}>Instructor</th>
              {slots.map(slot => (
                <th key={slot.row_key} style={{ ...TH, textAlign: 'center', minWidth: '110px' }}>
                  <div style={{ fontWeight: '600' }}>Slot {slot.row_key}</div>
                  <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px' }}>
                    {fmtTime(slot.begin)}–{fmtTime(slot.end)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredInstrs.map(instr => (
              <tr key={instr.row_key}>
                <td style={{ ...TD, fontWeight: '500' }}>
                  <div>{instr.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{instr.email}</div>
                </td>
                {slots.map(slot => {
                  const val = getPref(instr.row_key, slot.row_key);
                  const isDirty = pending[`${instr.row_key}|${slot.row_key}`] !== undefined;
                  return (
                    <td
                      key={slot.row_key}
                      style={{
                        ...TD,
                        textAlign: 'center',
                        background: PREF_COLORS[val],
                        outline: isDirty ? '2px solid var(--accent)' : 'none',
                        outlineOffset: '-2px',
                      }}
                    >
                      <select
                        value={val}
                        onChange={e => setPref(instr.row_key, slot.row_key, e.target.value as '1' | '2' | 'x' | '')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: PREF_TEXT_COLORS[val],
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        <option value="">—</option>
                        <option value="1">⭐ Preferred</option>
                        <option value="2">✅ Available</option>
                        <option value="x">🚫 Unavailable</option>
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Prerequisites sub-tab ──────────────────────────────────────────────────

function PrerequisitesTab() {
  const [prereqs, setPrereqs] = useState<PrerequisiteData[]>([]);
  const [courseCodes, setCourseCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [addCourse, setAddCourse] = useState('');
  const [addPrereq, setAddPrereq] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await schedulerApi.getPrerequisites();
      setPrereqs(data.prerequisites);
      setCourseCodes(data.course_codes);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (rowKey: string) => {
    if (!window.confirm('Remove this prerequisite?')) return;
    setDeleting(rowKey);
    try {
      await schedulerApi.deletePrerequisite(rowKey);
      setPrereqs(prev => prev.filter(p => p.row_key !== rowKey));
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleAdd = async () => {
    if (!addCourse || !addPrereq) return;
    setAdding(true); setAddError(null);
    try {
      const res = await schedulerApi.addPrerequisite(addCourse, addPrereq);
      setPrereqs(prev => [...prev, { row_key: res.row_key, course_id: addCourse, prereq_course_id: addPrereq }]
        .sort((a, b) => a.course_id.localeCompare(b.course_id)));
      setAddCourse(''); setAddPrereq('');
    } catch (e: any) { setAddError(e?.response?.data?.error || e?.message || 'Add failed'); }
    finally { setAdding(false); }
  };

  const filtered = prereqs.filter(p =>
    !filter || p.course_id.toLowerCase().includes(filter.toLowerCase()) ||
    p.prereq_course_id.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading prerequisites…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      <div style={{ ...CARD, marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px', color: 'var(--text-primary)' }}>Add Prerequisite</h4>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Course</label>
            <select value={addCourse} onChange={e => setAddCourse(e.target.value)} style={{ ...INPUT, minWidth: '150px' }}>
              <option value="">— Select course —</option>
              {courseCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>requires →</div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Prerequisite</label>
            <select value={addPrereq} onChange={e => setAddPrereq(e.target.value)} style={{ ...INPUT, minWidth: '150px' }}>
              <option value="">— Select prereq —</option>
              {courseCodes.filter(c => c !== addCourse).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button style={BTN('primary')} onClick={handleAdd} disabled={!addCourse || !addPrereq || adding}>
            {adding ? '…' : '+ Add'}
          </button>
        </div>
        {addError && <p style={{ margin: '0.5rem 0 0 0', color: '#f87171', fontSize: '12px' }}>❌ {addError}</p>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <input placeholder="Filter by course code…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...INPUT, width: '220px' }} />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{filtered.length} rule{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Course</th>
            <th style={TH}>Requires Prerequisite</th>
            <th style={TH}></th>
          </tr></thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.row_key}>
                <td style={{ ...TD, fontWeight: '600', color: 'var(--accent)' }}>{p.course_id}</td>
                <td style={TD}>{p.prereq_course_id}</td>
                <td style={TD}>
                  <button style={BTN('danger')} onClick={() => handleDelete(p.row_key)} disabled={deleting === p.row_key}>
                    {deleting === p.row_key ? '…' : '🗑 Remove'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={3} style={{ ...TD, textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No prerequisites found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Equivalencies sub-tab ───────────────────────────────────────────────────

function EquivalenciesTab() {
  const [equivs, setEquivs] = useState<EquivalencyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [addNew, setAddNew] = useState('');
  const [addOld, setAddOld] = useState('');
  const [addTitle, setAddTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await schedulerApi.getEquivalencies();
      setEquivs(data.equivalencies);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (rowKey: string) => {
    if (!window.confirm('Remove this equivalency?')) return;
    setDeleting(rowKey);
    try {
      await schedulerApi.deleteEquivalency(rowKey);
      setEquivs(prev => prev.filter(e => e.row_key !== rowKey));
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleAdd = async () => {
    if (!addNew || !addOld) return;
    setAdding(true); setAddError(null);
    try {
      const res = await schedulerApi.addEquivalency(addNew, addOld, addTitle);
      setEquivs(prev => [...prev, { row_key: res.row_key, new_code: addNew.toUpperCase(), old_code: addOld.toUpperCase(), course_title: addTitle }]
        .sort((a, b) => a.new_code.localeCompare(b.new_code)));
      setAddNew(''); setAddOld(''); setAddTitle('');
    } catch (e: any) { setAddError(e?.response?.data?.error || e?.message || 'Add failed'); }
    finally { setAdding(false); }
  };

  const filtered = equivs.filter(e =>
    !filter || e.new_code.toLowerCase().includes(filter.toLowerCase()) ||
    e.old_code.toLowerCase().includes(filter.toLowerCase()) ||
    e.course_title.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading equivalencies…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      <div style={{ ...CARD, marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px', color: 'var(--text-primary)' }}>Add Equivalency</h4>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Old Code</label>
            <input value={addOld} onChange={e => setAddOld(e.target.value.toUpperCase())} style={{ ...INPUT, width: '110px' }} placeholder="WDD330" />
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', paddingBottom: '4px' }}>→</div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>New Code</label>
            <input value={addNew} onChange={e => setAddNew(e.target.value.toUpperCase())} style={{ ...INPUT, width: '110px' }} placeholder="WDD335" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Course Title (optional)</label>
            <input value={addTitle} onChange={e => setAddTitle(e.target.value)} style={{ ...INPUT, width: '220px' }} placeholder="Advanced Javascript…" />
          </div>
          <button style={BTN('primary')} onClick={handleAdd} disabled={!addNew || !addOld || adding}>
            {adding ? '…' : '+ Add'}
          </button>
        </div>
        {addError && <p style={{ margin: '0.5rem 0 0 0', color: '#f87171', fontSize: '12px' }}>❌ {addError}</p>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <input placeholder="Filter by code or title…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...INPUT, width: '240px' }} />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{filtered.length} equivalenc{filtered.length !== 1 ? 'ies' : 'y'}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Old Code</th>
            <th style={TH}>New Code</th>
            <th style={TH}>Course Title</th>
            <th style={TH}></th>
          </tr></thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.row_key}>
                <td style={{ ...TD, color: 'var(--text-secondary)' }}>{e.old_code}</td>
                <td style={{ ...TD, fontWeight: '600', color: 'var(--accent)' }}>{e.new_code}</td>
                <td style={{ ...TD, fontSize: '12px' }}>{e.course_title || '—'}</td>
                <td style={TD}>
                  <button style={BTN('danger')} onClick={() => handleDelete(e.row_key)} disabled={deleting === e.row_key}>
                    {deleting === e.row_key ? '…' : '🗑 Remove'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} style={{ ...TD, textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No equivalencies found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Sequences sub-tab ───────────────────────────────────────────────────────

function SequencesTab() {
  const [sequences, setSequences] = useState<SequenceData[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await schedulerApi.getSequences();
      setSequences(data.sequences);
      setPrograms(data.programs);
      if (data.programs.length) setSelectedProgram(data.programs[0]);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const filtered = sequences.filter(s => {
    const matchProg = !selectedProgram || s.degree_program === selectedProgram;
    const matchText = !filter || s.course_id.toLowerCase().includes(filter.toLowerCase()) ||
      s.course_title.toLowerCase().includes(filter.toLowerCase());
    return matchProg && matchText;
  });

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading course sequences…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Program</label>
          <select value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)} style={{ ...INPUT, minWidth: '140px' }}>
            <option value="">All Programs</option>
            {programs.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Search</label>
          <input placeholder="Course code or title…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...INPUT, width: '220px' }} />
        </div>
        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          {filtered.length} course{filtered.length !== 1 ? 's' : ''} · {programs.length} programs
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...TH, textAlign: 'center', width: '70px' }}>Seq #</th>
            <th style={TH}>Course</th>
            <th style={TH}>Title</th>
            {!selectedProgram && <th style={TH}>Program</th>}
          </tr></thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.row_key}>
                <td style={{ ...TD, textAlign: 'center', fontWeight: '700', color: 'var(--text-secondary)' }}>{s.sequence_number}</td>
                <td style={{ ...TD, fontWeight: '600', color: 'var(--accent)' }}>{s.course_id}</td>
                <td style={{ ...TD, fontSize: '12px' }}>{s.course_title}</td>
                {!selectedProgram && <td style={{ ...TD, fontSize: '12px', color: 'var(--text-secondary)' }}>{s.degree_program}</td>}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} style={{ ...TD, textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No sequences found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main DataManagementPanel ────────────────────────────────────────────────

// ─── Courses sub-tab (read-only) ─────────────────────────────────────────────

function CoursesTab() {
  const [courses, setCourses] = useState<import('../../services/scheduler-api').CourseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await schedulerApi.getCourses();
      setCourses(data.courses);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const filtered = courses.filter(c =>
    !filter ||
    (c.new_course_code ?? '').toLowerCase().includes(filter.toLowerCase()) ||
    c.course_code.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading courses…</p>;
  if (error) return <p style={{ color: '#f87171' }}>❌ {error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          placeholder="Filter by course code…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ ...INPUT, width: '240px' }}
        />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{filtered.length} course{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>New Code</th>
            <th style={TH}>Old Code</th>
            <th style={{ ...TH, textAlign: 'center' }}>Cengage</th>
          </tr></thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.row_key}>
                <td style={{ ...TD, fontWeight: '600', color: 'var(--accent)' }}>
                  {(c.new_course_code || c.course_code).toUpperCase()}
                </td>
                <td style={{ ...TD, fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {c.new_course_code ? c.course_code.toUpperCase() : '—'}
                </td>
                <td style={{ ...TD, textAlign: 'center' }}>
                  {c.cengage_indicator === 'Y'
                    ? <span style={{ color: '#4ade80', fontSize: '12px', fontWeight: '600' }}>✅ Yes</span>
                    : <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={3} style={{ ...TD, textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No courses found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type SubTab = 'instructors' | 'credentials' | 'slots' | 'prerequisites' | 'equivalencies' | 'sequences' | 'courses';

export function DataManagementPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const dataBasePath = location.pathname.startsWith('/ECM') ? '/ECM/Dashboard/scheduler/data' : '/Scheduler/data';
  const subTab = ((): SubTab => {
    const suffix = location.pathname.slice(dataBasePath.length).replace(/^\//, '');
    if (suffix === 'credentials') return 'credentials';
    if (suffix === 'slots') return 'slots';
    if (suffix === 'prerequisites') return 'prerequisites';
    if (suffix === 'equivalencies') return 'equivalencies';
    if (suffix === 'sequences') return 'sequences';
    if (suffix === 'courses') return 'courses';
    return 'instructors';
  })();

  const SUB_TABS: { id: SubTab; label: string }[] = [
    { id: 'instructors',   label: '👤 Instructors' },
    { id: 'credentials',  label: '📚 Credentials' },
    { id: 'slots',        label: '🕐 Slot Prefs' },
    { id: 'prerequisites',label: '🔗 Prerequisites' },
    { id: 'equivalencies',label: '↔️ Equivalencies' },
    { id: 'sequences',    label: '📋 Sequences' },
    { id: 'courses',      label: '📘 Courses' },
  ];

  const DESCRIPTIONS: Record<SubTab, string> = {
    instructors:   'Manage instructor availability flags and notes. Changes are saved to Azure immediately.',
    credentials:   'Manage which instructors are qualified to teach each course — add or remove credential mappings.',
    slots:         'Set each instructor\'s time slot preferences. ⭐ Preferred = first choice, ✅ Available = acceptable, 🚫 Unavailable = cannot teach. Click "Save All" to commit changes.',
    prerequisites: 'Manage course prerequisites — which courses a student must complete before enrolling in another.',
    equivalencies: 'Course code equivalencies: map old course codes to new codes for transcript matching.',
    sequences:     'Course sequences define the order courses appear in each degree program (read-only).',
    courses:       'Full course catalog from facman2. Shows current and legacy course codes and Cengage access requirements (read-only).',
  };

  return (
    <div style={CARD}>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '1.5rem', borderBottom: '1px solid var(--card-border)', paddingBottom: '0', flexWrap: 'wrap' }}>
        {SUB_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.id === 'instructors' ? dataBasePath : `${dataBasePath}/${tab.id}`)}
            style={{
              padding: '7px 14px',
              border: 'none',
              borderBottom: `3px solid ${subTab === tab.id ? 'var(--accent)' : 'transparent'}`,
              background: 'transparent',
              color: subTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: subTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <p style={{ margin: '0 0 1.25rem 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
        {DESCRIPTIONS[subTab]}
      </p>

      {subTab === 'instructors'   && <InstructorsTab />}
      {subTab === 'credentials'   && <CredentialsTab />}
      {subTab === 'slots'         && <SlotPrefsTab />}
      {subTab === 'prerequisites' && <PrerequisitesTab />}
      {subTab === 'equivalencies' && <EquivalenciesTab />}
      {subTab === 'sequences'     && <SequencesTab />}
      {subTab === 'courses'       && <CoursesTab />}
    </div>
  );
}
